import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check } from 'lucide-react';
import { adminAlertsService } from '../../services/adminAlertsService';
import { realtimeService } from '../../services/realtimeService';
import { useNavigate } from 'react-router-dom';

export function AdminAlertsDropdown() {
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Load initial alerts and unread count
  useEffect(() => {
    loadAlerts();
    loadUnreadCount();
  }, []);

  // Subscribe to real-time alert updates
  useEffect(() => {
    const subscription = realtimeService?.subscribeToAdminAlerts((payload) => {
      if (payload?.eventType === 'INSERT') {
        const newAlert = payload?.new;
        setAlerts(prev => [newAlert, ...prev]?.slice(0, 10));
        setUnreadCount(prev => prev + 1);
      }
    });

    return () => {
      realtimeService?.unsubscribe('admin-alerts-realtime');
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const { alerts: data } = await adminAlertsService?.getAlerts({ limit: 10 });
      setAlerts(data);
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const count = await adminAlertsService?.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const handleMarkAsRead = async (alertId, event) => {
    event?.stopPropagation();
    try {
      await adminAlertsService?.markAsRead(alertId);
      setAlerts(prev => prev?.map(alert => 
        alert?.id === alertId ? { ...alert, is_read: true } : alert
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking alert as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await adminAlertsService?.markAllAsRead();
      setAlerts(prev => prev?.map(alert => ({ ...alert, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleAlertClick = (alert) => {
    // Mark as read when clicking
    if (!alert?.is_read) {
      handleMarkAsRead(alert?.id, { stopPropagation: () => {} });
    }

    // Navigate to relevant page based on entity type
    if (alert?.entity_type && alert?.entity_id) {
      switch (alert?.entity_type) {
        case 'container':
          navigate(`/admin/containers/${alert?.entity_id}`);
          break;
        case 'shipment':
          navigate(`/admin/shipments/${alert?.entity_id}`);
          break;
        case 'pickup_task':
          navigate(`/admin/pickup-tasks`);
          break;
        case 'delivery_task':
          navigate(`/admin/delivery-tasks`);
          break;
        default:
          break;
      }
    }
    
    setIsOpen(false);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error':
        return 'text-red-600 bg-red-50';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50';
      case 'info':
      default:
        return 'text-blue-600 bg-blue-50';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / 60000);

    if (diffInMinutes < 1) return 'À l\'instant';
    if (diffInMinutes < 60) return `Il y a ${diffInMinutes} min`;
    if (diffInMinutes < 1440) return `Il y a ${Math.floor(diffInMinutes / 60)} h`;
    return date?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon with Badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-600 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Tout marquer comme lu
              </button>
            )}
          </div>

          {/* Alerts List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-8 text-center text-gray-500">
                Chargement...
              </div>
            ) : alerts?.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                Aucune notification
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {alerts?.map((alert) => (
                  <div
                    key={alert?.id}
                    onClick={() => handleAlertClick(alert)}
                    className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                      !alert?.is_read ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-1 p-2 rounded-full ${getSeverityColor(alert?.severity)}`}>
                        <div className="w-2 h-2 rounded-full bg-current" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium ${!alert?.is_read ? 'text-gray-900' : 'text-gray-700'}`}>
                            {alert?.title}
                          </p>
                          {!alert?.is_read && (
                            <button
                              onClick={(e) => handleMarkAsRead(alert?.id, e)}
                              className="text-blue-600 hover:text-blue-700"
                              title="Marquer comme lu"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{alert?.message}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {formatTimestamp(alert?.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-200">
            <button
              onClick={() => {
                navigate('/admin/notifications');
                setIsOpen(false);
              }}
              className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Voir toutes les notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
}